import { mtrefYearHeadings } from '@/const';

import { countWords } from '../wordCount';
import { normalizeName } from '../resolve';

const SECTION_WORD_LIMIT = 15000;
const PREVIEW_DESCRIPTION_CHARS = 120;

// Shares the budget table headings so the template columns read the same as the
// editor, which already carries the R'000 unit.
const budgetColumns = mtrefYearHeadings.map((heading, index) => ({
  header: `Capital Budget ${heading}`,
  key: `budgetYear${index + 1}`,
  required: false,
  type: 'number',
  width: 40,
  numFmt: '#,##0.00',
  validateValue: (value) => value < 0 ? 'Budget amounts cannot be negative.' : null,
}));

/**
 * Flattens a stored HTML description into a short plain-text snippet for the
 * confirmation table, which renders cell values as literal text.
 *
 * @param {string|null} html Paragraph-wrapped description
 * @returns {string}
 */
const previewDescription = (html) => {
  const text = String(html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

  return text.length > PREVIEW_DESCRIPTION_CHARS ? `${text.slice(0, PREVIEW_DESCRIPTION_CHARS)}…` : text;
};

/**
 * Import definition for municipal programmes and projects on the
 * Implementation Plan page.
 * Target: planStore.content.implementationPlan.municipalProgrammesAndProjects
 *
 * Sections are referenced by name and auto-created when no existing section
 * matches; development strategies must match an existing strategy. Projects
 * have no name of their own — the description stands in for one and is
 * required, but two projects may legitimately share the same wording.
 */
export const projectsImport = {
  type: 'projects',
  sheetName: 'Projects',
  templateFileName: 'projects-template.xlsx',
  noun: { singular: 'project', plural: 'projects' },
  replaceWarning: 'Importing will replace all existing projects in every section of this plan, including any that were added manually. Sections that are not mentioned in the file will be kept, but emptied of projects.',

  columns: [
    { header: 'Section', key: 'section', required: true, type: 'string', width: 32, link: 'section' },
    { header: 'Development Strategy', key: 'strategy', required: true, type: 'string', width: 32, link: 'developmentStrategy' },
    { header: 'Description', key: 'description', required: true, type: 'text', width: 64, wordLimit: 1000 },
    { header: 'Ward Location', key: 'wardLocation', required: false, type: 'string', width: 24 },
    {
      header: 'Latitude',
      key: 'latitude',
      required: false,
      type: 'number',
      width: 14,
      validateValue: (value) => (value < -90 || value > 90) ? 'Latitude must be between -90 and 90.' : null,
    },
    {
      header: 'Longitude',
      key: 'longitude',
      required: false,
      type: 'number',
      width: 14,
      validateValue: (value) => (value < -180 || value > 180) ? 'Longitude must be between -180 and 180.' : null,
    },
    {
      header: 'Total Project Value (R\'000)',
      key: 'totalProjectValue',
      required: false,
      type: 'number',
      width: 28,
      numFmt: '#,##0.00',
      validateValue: (value) => value < 0 ? 'The total project value cannot be negative.' : null,
    },
    ...budgetColumns,
  ],

  previewColumns: [
    { key: 'section', label: 'Section' },
    { key: 'description', label: 'Description' },
    { key: 'strategy', label: 'Development Strategy' },
  ],

  buildContext: (planStore) => ({
    section: {
      noun: 'section',
      autoCreate: true,
      candidates: planStore.content.implementationPlan.municipalProgrammesAndProjects
        .map(section => ({ id: section.id, name: section.sectionName })),
    },
    developmentStrategy: {
      noun: 'development strategy',
      candidates: planStore.content.developmentStrategies
        .map(strategy => ({ id: strategy.id, name: strategy.name })),
    },
  }),

  validateRows: (rows, addError) => {
    rows.forEach(({ excelRow, values }) => {
      if (values.latitude !== null && values.longitude === null) {
        addError(excelRow, 'Longitude', 'Longitude is required when a latitude is provided.');
      }

      if (values.longitude !== null && values.latitude === null) {
        addError(excelRow, 'Latitude', 'Latitude is required when a longitude is provided.');
      }
    });
  },

  buildResult: (rows, context, nextId) => {
    const sections = context.section.candidates
      .map(candidate => ({ id: candidate.id, sectionName: candidate.name, projects: [] }));

    const sectionsById = new Map(sections.map(section => [section.id, section]));
    const newSectionsByName = new Map();
    const preview = [];

    rows.forEach(({ values, links }) => {
      let section;

      if (links.section) {
        section = sectionsById.get(links.section.id);
      } else {
        const nameKey = normalizeName(values.section);
        section = newSectionsByName.get(nameKey);

        if (!section) {
          section = { id: nextId(), sectionName: values.section, projects: [] };
          newSectionsByName.set(nameKey, section);
          sections.push(section);
        }
      }

      section.projects.push({
        id: nextId(),
        description: values.description,
        developmentStrategyId: links.strategy.id,
        wardLocation: values.wardLocation,
        latitude: values.latitude === null ? null : String(values.latitude),
        longitude: values.longitude === null ? null : String(values.longitude),
        totalProjectValue: values.totalProjectValue,
        budget: {
          capital: {
            year1: values.budgetYear1,
            year2: values.budgetYear2,
            year3: values.budgetYear3,
          },
        },
      });

      preview.push({
        section: section.sectionName,
        description: previewDescription(values.description),
        strategy: links.strategy.name,
      });
    });

    return {
      sections,
      preview,
      projectCount: rows.length,
      populatedSectionCount: sections.filter(section => section.projects.length > 0).length,
      newSectionCount: newSectionsByName.size,
    };
  },

  validateSectionLimit: (result, planStore) => {
    const wouldBeSection = { ...planStore.content.implementationPlan, municipalProgrammesAndProjects: result.sections };
    const words = countWords(wouldBeSection);

    if (words > SECTION_WORD_LIMIT) {
      return `This import would put the Implementation Plan section over its ${SECTION_WORD_LIMIT}-word limit (${words} words). Reduce the content and try again.`;
    }

    return null;
  },

  previewRows: (result) => result.preview,

  summarize: (result) => {
    const projects = `${result.projectCount} ${result.projectCount === 1 ? 'project' : 'projects'}`;
    const sections = `${result.populatedSectionCount} ${result.populatedSectionCount === 1 ? 'section' : 'sections'}`;
    const newSections = result.newSectionCount > 0 ? ` (${result.newSectionCount} new)` : '';

    return `${projects} across ${sections}${newSections} ready to import`;
  },

  commit: (planStore, result) => {
    planStore.content.implementationPlan.municipalProgrammesAndProjects = result.sections;
  },
};
